import { SafeCapType } from 'shared';
import { z } from 'zod';

import { DilutionImpact } from '../../../types/scenario-modeling.types';
import { getTranslation } from '../../../utils/get-translation.util';
import {
  grantsFormSchema,
  safesFormSchema,
  sarsFormSchema,
  valuationFormSchema,
} from './scenario-details.validation';

export const poolsFormSchema = z.object({
  poolItems: z.array(
    z.object({
      id: z.string().trim().optional(),
      name: z
        .string()
        .trim()
        .min(1, { message: getTranslation('REQUIRED') })
        .max(30, { message: getTranslation('MAXIMUM_CHARACTERS', { count: 30 }) }),
      dilutedShares: z.coerce
        .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
        .min(1, { message: getTranslation('REQUIRED') }),
      type: z.enum(DilutionImpact),
      shareClass: z.object({
        name: z
          .string()
          .trim()
          .min(1, { message: getTranslation('REQUIRED') }),
        id: z
          .string()
          .trim()
          .min(1, { message: getTranslation('REQUIRED') }),
      }),
    }),
  ),
});

export const customSafesFormSchema = z.object({
  safeItems: z.array(
    z
      .object({
        id: z.string().trim().optional(),
        investment: z.coerce
          .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
          .min(1, { message: getTranslation('REQUIRED') }),
        cap: z.coerce
          .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
          .min(0, { message: getTranslation('REQUIRED') })
          .optional(),
        capType: z.enum(SafeCapType).nullable().optional(),
        floor: z.coerce
          .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
          .optional(),
        discount: z.coerce
          .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
          .max(100, { message: getTranslation('MAXIMUM_100') })
          .optional(),
        shareClass: z.object({
          name: z
            .string()
            .trim()
            .min(1, { message: getTranslation('REQUIRED') }),
          id: z
            .string()
            .trim()
            .min(1, { message: getTranslation('REQUIRED') }),
        }),
        stakeholder: z.object({
          fullName: z
            .string()
            .trim()
            .min(1, { message: getTranslation('REQUIRED') }),
          id: z.string().trim(),
        }),
      })
      .refine(({ cap, discount }) => {
        return (cap && cap > 0) || (discount && discount > 0);
      })
      // A real cap needs its type; the placeholder value (`defaultNumericValue`) counts as no cap.
      .refine(({ cap, capType }) => !(cap && cap >= 1) || Boolean(capType), {
        message: getTranslation('CAP_TYPE_REQUIRED'),
        path: ['capType'],
      }),
  ),
});

export const investmentsFormSchema = z.object({
  investmentItems: z.array(
    z.object({
      id: z.string().trim().optional(),
      investment: z.coerce
        .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
        .min(1, { message: getTranslation('REQUIRED') }),
      shareClass: z.object({
        name: z
          .string()
          .trim()
          .min(1, { message: getTranslation('REQUIRED') }),
        id: z
          .string()
          .trim()
          .min(1, { message: getTranslation('REQUIRED') }),
      }),
      stakeholder: z.object({
        fullName: z
          .string()
          .trim()
          .min(1, { message: getTranslation('REQUIRED') }),
        id: z.string().trim(),
      }),
    }),
  ),
});

export const fundraisingFormSchema = z.object({
  valuation: valuationFormSchema,
  pools: poolsFormSchema.optional(),
  customSafes: customSafesFormSchema.optional(),
  safes: safesFormSchema.optional(),
  investments: investmentsFormSchema.optional(),
  grants: grantsFormSchema.optional(),
  sars: sarsFormSchema.optional(),
});

export type FundraisingFormSchema = z.infer<typeof fundraisingFormSchema>;
